import React from 'react';
import { omit } from 'lodash';
import { graphql, navigate, useStaticQuery } from 'gatsby';

import type { ContentfulAsset, ContentfulEntity, Localizable, Settings } from '@models/contentful';
import { type AppState, initialAppState } from '@context/appState';
import {
    availableLocales,
    defaultLocale,
    fromMsLocale,
    isContentAvailableForLocale,
    isLocaleAvailable,
    localeSlug,
    PAGE_TYPES,
    toApiLocale
} from '@helpers/shared/common';
import { useMicrositeMessaging } from '@hooks/microsite';
import { NavigationData } from '@models';
import MicrocopyHelper from '@helpers/microcopy';
import MultiLanguageHelper from '@helpers/shared/multi-language-helper';

export const AppStateContext = React.createContext<AppState>(initialAppState);

interface AppProviderProps {
    children: React.ReactElement;
}

// Note: useStaticQuery requires static queries and doesn't support template literal interpolation.
// The "bruynzeel" values below should match the projectTag constant from @helpers/shared/common
// which is currently: 'bruynzeel'
// If projectTag changes, all occurrences of "bruynzeel" in this query must be updated manually.

export const AppProvider = ({ children }: AppProviderProps) => {
    const data = useStaticQuery(graphql`
        query {
            assets: allContentfulAsset(
                filter: { metadata: { tags: { elemMatch: { contentful_id: { eq: "bruynzeel" } } } } } 
            ) {
                ...assetNodes
            }
            pages: allContentfulContentPage(
                filter: { metadata: { tags: { elemMatch: { contentful_id: { eq: "bruynzeel" } } } } } 
            ) {
                nodes {
                    id: contentful_id
                    locale: node_locale
                    slug
                }
            }
            catalogs: allContentfulCatalogPage(
                filter: { metadata: { tags: { elemMatch: { contentful_id: { eq: "bruynzeel" } } } } } 
            ) {
                nodes {
                    id: contentful_id
                    locale: node_locale
                    slug
                    parentCatalogPage {
                        id: contentful_id
                        locale: node_locale
                        slug
                    }
                }
            }
            blogs: allContentfulBlogPage(
                filter: { metadata: { tags: { elemMatch: { contentful_id: { eq: "bruynzeel" } } } } } 
            ) {                
                nodes {
                    id: contentful_id
                    locale: node_locale
                    slug
                    languages {
                        locales
                    }
                }
            }
            products: allThujaProduct { 
                 nodes {
                    id
                    slug: ean
                    slug_nl_NL
                    slug_nl_BE
                    slug_fr_BE
                } 
            }
            settings: allContentfulSettings(
                filter: { metadata: { tags: { elemMatch: { contentful_id: { eq: "bruynzeel" } } } } }
                limit: 1
            ) {
                nodes {
                    ...settings
                }
            },
            microcopy: allContentfulResourceSet(
                filter: { 
                    name: { in: [ "common_bruynzeel", "megaMenu_bruynzeel", "footer_bruynzeel" ] },
                    metadata: { tags: { elemMatch: { contentful_id: { eq: "bruynzeel" } } } }
                }
            ) {
                ...resourceNodes
            }
        }
    `);
    const { onMessage } = useMicrositeMessaging();

    // Process assets only once using useMemo - static data from GraphQL
    const processedAssets = React.useMemo(() => {
        return data.assets.nodes.reduce((acc: any, node: ContentfulAsset) => {
            acc[node.id] = omit(node, [ 'id' ]);
            return acc;
        }, {});
    }, [ data.assets.nodes ]);

    // region [pages]
    // make a structure to find full page slug based on pageId and locale
    // Process pages only once using useMemo - static data from GraphQL
    const processedContentPages = React.useMemo(() => {
        return data.pages.nodes
            .filter((page: ContentfulEntity) =>isLocaleAvailable(page.locale))
            .reduce((acc: any, node: ContentfulEntity) => {
                acc[node.id] ||= {};
                acc[node.id][node.locale] = node.slug;
                return acc;
            }, {});
    }, [ data.pages.nodes ]);

    // nested structure for catalog pages
    const pagesWithParents = React.useMemo(() => {
        // Create a map of catalog pages by id for quick lookup
        const catalogMap = new Map<string, ContentfulEntity & { parent?: ContentfulEntity }>();

        const pageKey = (page: ContentfulEntity) => `${ page.slug }_${ page.locale }`;

        data.catalogs.nodes.forEach((page: ContentfulEntity) => {
            catalogMap.set(pageKey(page), { ...page });
        });

        const getPageWithParent = (page: ContentfulEntity) => {
            const parentCatalogPage = page.parentCatalogPage;
            const parent = parentCatalogPage?.id ? catalogMap.get(pageKey(parentCatalogPage)) : null;

            return {
                ...page,
                parent: parent ? getPageWithParent(parent) : null
            };
        }

        // Build parent relationships without mutating original data
        return data.catalogs.nodes.map((page: ContentfulEntity) => getPageWithParent(page));
    }, [ data.catalogs.nodes ]);

    const processedCatalogPages = React.useMemo(() => {
        // Build URLs by traversing parent chain
        const buildUrl = (page: ContentfulEntity & { parent?: ContentfulEntity }): string => {
            const urlParts: string[] = [];
            let current: ContentfulEntity & { parent?: ContentfulEntity } | undefined = page;

            while (current) {
                urlParts.unshift(current.slug);
                current = current.parent;
            }

            return urlParts.join('/');
        };

        // Merge catalog pages into the pages object
        return pagesWithParents
            .filter((page: ContentfulEntity) => isLocaleAvailable(page.locale))
            .reduce((acc: any, node: ContentfulEntity & { parent?: ContentfulEntity }) => {
                acc[node.id] ||= {};
                acc[node.id][node.locale] = buildUrl(node);
                return acc;
            }, {});
    }, [ pagesWithParents ]);

    const processedBlogPages = React.useMemo(() => {
        return data.blogs.nodes
            .filter((page: ContentfulEntity & Localizable) =>
                isLocaleAvailable(page.locale) && isContentAvailableForLocale(page, page.locale)
            )
            .reduce((acc: any, node: ContentfulEntity) => {
                acc[node.id] ||= {};
                acc[node.id][node.locale] = `${ PAGE_TYPES.BLOG }/${ node.slug }`;
                return acc;
            }, {});
    }, [ data.blogs.nodes ]);

    const processedPDPs = React.useMemo(() => {
        return data.products.nodes
            .reduce((acc: any, node: ContentfulEntity) => {
               acc[node.id] = availableLocales.reduce((acc: any, locale) => {
                   acc[locale] = `${ PAGE_TYPES.PDP }/${ MultiLanguageHelper.localize(node, 'slug', toApiLocale(locale)) }`; // TODO align locales
                   return acc;
               }, {});

               return acc;
            }, {});
    }, [ data.products.nodes ]);

    const processedPages = React.useMemo(() => {
        return { ...processedContentPages, ...processedCatalogPages, ...processedBlogPages, ...processedPDPs };
    }, [ processedContentPages, processedCatalogPages, processedBlogPages, processedPDPs ]);
    // endregion

    // region [urls]
    // make a structure to find page id based on localized slug
    // Process pages only once using useMemo - static data from GraphQL
    const processedContentUrls = React.useMemo(() => {
        return data.pages.nodes.reduce((acc: any, node: ContentfulEntity) => {
            acc[node.slug] = node.id;
            return acc;
        }, {});
    }, [ data.pages.nodes ]);

    const processedCatalogUrls = React.useMemo(() => {
        return data.catalogs.nodes.reduce((acc: any, node: ContentfulEntity) => {
            acc[node.slug] = node.id;
            return acc;
        }, {});
    }, [ data.catalogs.nodes ]);

    const processedBlogUrls = React.useMemo(() => {
        return data.blogs.nodes.reduce((acc: any, node: ContentfulEntity) => {
            acc[node.slug] = node.id;
            return acc;
        }, {});
    }, [ data.blogs.nodes ]);

    const processedPDPUrls = React.useMemo(() => {
        return data.products.nodes
            .reduce((acc: any, node: ContentfulEntity) => {
                acc[node.slug] = node.id;
                return acc;
            }, {});
    }, [ data.products.nodes ]);

    const processedUrls = React.useMemo(() => {
        return { ...processedContentUrls, ...processedCatalogUrls, ...processedBlogUrls, ...processedPDPUrls };
    }, [ processedContentUrls, processedCatalogUrls, processedBlogUrls, processedPDPUrls ]);
    // endregion

    // Process settings - get first settings entry as default
    const processedSettings: Settings | undefined = React.useMemo(() => {
        return data.settings?.nodes?.[0] || undefined;
    }, [ data.settings?.nodes ]);

    // Cache static data so it is calculated only once
    const appState: AppState = React.useMemo(() => {
        return {
            ...initialAppState,
            assets: processedAssets,
            pages: processedPages,
            urls: processedUrls,
            settings: processedSettings,
            microcopy: new MicrocopyHelper(defaultLocale).add(data.microcopy?.nodes || []),
        };
    }, [ processedAssets, processedPages, processedUrls, processedSettings ]);

    // TODO make it happen only once and do not re-register on navigation
    onMessage('navigation', (data: NavigationData) => {
        if (data.page === 'product') {
            const pageId = processedUrls[data.data.ean];
            const locale = fromMsLocale(data.data.locale); // TODO align locales
            const url = processedPages[pageId][locale];

            navigate(`${ localeSlug(locale) }/${ url }`);
        }


        // TODO navigation for other pages
        if (data.page === 'catalog') {
        }
    });

    return (
        <AppStateContext.Provider value={ appState }>
            { children }
        </AppStateContext.Provider>
    );
};

export const useAppState = (): AppState => React.useContext(AppStateContext);
